<script lang="ts">
  import RadioSelect from '$lib/components/control/RadioSelect.svelte';
  import { getPrices } from '$lib/gateway/netlifyFunctions';
  import { onMount } from 'svelte';
  import { setSelectedPlan, getSelectedPlan } from '$lib/helpers/cookieHelpers';
  import Cookies from 'js-cookie';
  import { currencyFormatter, currencyNumberFormatter } from '$lib/helpers/stringFormatters';
  import { getPromoCode } from '$lib/gateway/stripeClient';
  import type { GetPromoCodeResponse } from '../../../routes/api/stripe/promo/[code]/+server';

  export let data;
  export let price = undefined;
  export let country: string | undefined = undefined;
  export let timedPromoCode: GetPromoCodeResponse | undefined = undefined;
  let priceId = undefined;
  let prices;
  $: if (priceId && prices) price = prices?.find((price) => price.id === priceId);

  $: options = prices?.map((price) => ({
    value: price.id,
    label: price.label,
    subLabel: price.subLabel,
    badge: price.badge,
    currency: price.currency,
    price: price.unitAmount,
    salePrice: price.salePrice,
    originalPrice: price.originalPrice,
    plan: price.recurring?.interval,
    trialPeriodDays: price.trialPeriodDays,
  }));

  // Fetch country and then relevant prices
  $: fetchPrices(country);

  const PLAN_FORMATS = {
    CALCULATED_MONTHLY_PRICE: 'Calculated Monthly Price',
  };

  async function fetchPrices(country?: string) {
    // Prevent run on server
    if (typeof window === 'undefined') return;

    const priceIds = data?.pageData?.stripeProduct?.prices
      ?.filter((price) => price.enabled)
      ?.map((price) => price.id);

    prices = await Promise.all(
      (await getPrices(data?.pageData?.stripeProduct.id, priceIds, country))?.map(async (price) => {
        const contentfulPrice = data?.pageData?.stripeProduct?.prices?.find(
          (p) => p.id === price.id,
        );

        let priceUpdate = {};

        if (
          data?.pageData?.planFormat &&
          data?.pageData?.planFormat === PLAN_FORMATS.CALCULATED_MONTHLY_PRICE
        ) {
          priceUpdate = {
            label:
              price?.recurring?.interval === 'month'
                ? 'Monthly'
                : `Yearly • ${currencyNumberFormatter(price.unitAmount / 12, price.currency)}/month`,
            subLabel: `${currencyNumberFormatter(price.unitAmount, price.currency)}/${price.recurring?.interval} billed ${price?.recurring?.interval === 'month' ? `monthly` : `annually`}`,
          };
        }

        // Update price labels if promo code is available
        if (timedPromoCode || contentfulPrice?.promotion_code) {
          const promoCodeData =
            timedPromoCode || (await getPromoCode(contentfulPrice?.promotion_code.trim()))?.data;
          let monthFormatter =
            promoCodeData?.duration_in_months && promoCodeData?.duration_in_months > 1
              ? `${price.recurring?.interval}s`
              : `${price.recurring?.interval}`;
          let monthDuration =
            promoCodeData?.duration_in_months !== null ? promoCodeData?.duration_in_months : ``;
          const percentOff = promoCodeData?.percent_off || 0;
          const amountOff = promoCodeData?.amount_off || 0;
          if (percentOff > 0) {
            const salePrice = price.unitAmount - (price.unitAmount * percentOff) / 100;
            priceUpdate = {
              salePrice: salePrice,
              originalPrice: `${currencyNumberFormatter(price.unitAmount, price.currency)}`,
              label: `${currencyFormatter(salePrice, price.currency, true)} ${price.recurring?.interval === 'month' ? `per month` : `for your 1st year`}`,
              subLabel: `${
                price.recurring?.interval === 'month'
                  ? `for your 1st ${monthDuration} ${monthFormatter}. Then auto-renews at ${currencyFormatter(price.unitAmount, price.currency, true)} per ${price.recurring?.interval}.`
                  : `Then auto-renews at ${currencyFormatter(price.unitAmount, price.currency, true)} per ${price.recurring?.interval}.`
              }`,
            };
          }
          //Handle amounutOff coupons
          if (amountOff > 0) {
            const salePrice = price.unitAmount - amountOff;
            priceUpdate = {
              salePrice: salePrice,
              originalPrice: `${currencyNumberFormatter(price.unitAmount, price.currency)}`,
              label: `${currencyFormatter(salePrice, price.currency, true)} ${price.recurring?.interval === 'month' ? `per month` : `for your 1st year`}`,
              subLabel: `${
                price.recurring?.interval === 'month'
                  ? `for your 1st ${monthDuration} ${monthFormatter}. Then auto-renews at ${currencyFormatter(price.unitAmount, price.currency, true)} per ${price.recurring?.interval}.`
                  : `Then auto-renews at ${currencyFormatter(price.unitAmount, price.currency, true)} per ${price.recurring?.interval}.`
              }`,
            };
          }
        }

        return {
          ...price,
          trialPeriodDays: contentfulPrice?.trial_period_days || 0,
          promotionCode: timedPromoCode?.code || contentfulPrice?.promotion_code || '',
          ...priceUpdate,
        };
      }),
    );

    // Check selected plan
    if (priceId) {
      priceId = prices?.find((price) => price.id === priceId)?.id;
    }

    // Default plan
    if (!priceId) priceId = prices?.find((price) => price.recurring?.interval === 'month')?.id;
  }

  async function saveSelectedPlan(newPriceId) {
    await setSelectedPlan(Cookies, newPriceId as any);
  }

  $: if (priceId) saveSelectedPlan(priceId);

  onMount(async () => {
    // Load selected plan
    if (!priceId) {
      priceId = (await getSelectedPlan(Cookies)) as any;
    }
  });
</script>

<div>
  <p class="text-sm font-bold mb-4">1. Choose a plan</p>

  {#if data?.pageData.planText}
    <p class="text-xl text-primary-500 font-bold">{data?.pageData.planText}</p>
  {/if}

  {#if data?.pageData.planSubText}
    <p class="text-sm text-gray-500">{data?.pageData.planSubText}</p>
  {/if}

  <!-- Show options or skeleton -->
  {#if options}
    <RadioSelect customClass="mt-8" {options} bind:value={priceId} />
  {:else}
    <div class="flex flex-col mt-8">
      <div class="animate-pulse bg-gray-200 h-32 rounded-sm w-full"></div>
    </div>
  {/if}
</div>
